import Vue from 'vue';
import { config } from '@/scripts/tools/config';
import storage from '@/scripts/tools/storage';

const localStorageMap = {
  layout: true,
  userInfo: true,
  crumbs: true,
  showSideBar: true,
  // uploadNum: true,
  // downloadNum: true
};

const localStore = storage.get('store', 'session') || {};
const saveStore = data => {
  const saveLocalStorage = {};
  Object.keys(data).forEach(key => {
    if (localStorageMap[key]) {
      saveLocalStorage[key] = data[key];
    }
  });
  storage.add('userInfo', saveLocalStorage.userInfo, 'local');
  storage.add('store', saveLocalStorage, 'session');
};

const { location } = window;
const mainDomain = location.host.replace(/^\w+./, '');
const cosbrowserDomain = `cosbrowser.${mainDomain}`;
const officialDomain = mainDomain === 'tencentcloud.com' ? `www.${mainDomain}` : mainDomain;
const consoleDomain = `console.${mainDomain}`;
// 新增：支持的语言列表和 URL 参数解析
const supportedLanguages = ['zh', 'en'];
const urlParams = new URLSearchParams(window.location.search);
const langFromUrl = urlParams.get('lang');

// 根据优先级初始化语言
const isInternationalDomain = mainDomain === 'tencentcloud.com';
let localeLanguage;
// 修正后的优先级逻辑：区分用户设置和系统默认
const userLanguageSetting = window.localStorage.getItem('general.Language') || window.sessionStorage.getItem('general.Language');
if (langFromUrl && supportedLanguages.includes(langFromUrl)) {
  localeLanguage = langFromUrl; // 优先级1：URL 参数
} else if (userLanguageSetting && supportedLanguages.includes(userLanguageSetting)) {
  localeLanguage = userLanguageSetting; // 优先级2：用户主动设置（直接读取存储，避免默认值）
} else if (isInternationalDomain) {
  localeLanguage = 'en'; // 优先级3：国际站域名默认英文
} else {
  localeLanguage = config.defaultLanguage; // 优先级4：浏览器默认语言
}
// 兜底检查：确保语言在支持列表中
if (!supportedLanguages.includes(localeLanguage)) {
  console.log('  ⚠️ 语言不支持，回退到中文:', localeLanguage, '→ zh');
  localeLanguage = 'zh'; // 回退到默认支持的语言
}

/**
 * 初始化 store
 */
const state = {
  localeLanguage,
  officialDomain, // 官网主站域名
  consoleDomain, // 控制台域名
  cosbrowserServerUrl: `https://${cosbrowserDomain}`,
  cos5ServerPrefix: `https://cos5.${mainDomain}/cosbrowser/`,
  oauthPrefix: 'https://cos5.cloud.tencent.com/cosbrowser/',

  layout: localStore.layout || 'grid',
  userInfo: {},
  isLogin: false,

  // 面包屑导航
  crumbs: [],
  // 上传文件数
  uploadNum: 0,
  // 下载文件数
  downloadNum: 0,

  showRegion: true,
  showSideBar: localStore.showSideBar || false, // 显示/隐藏侧边栏
  checkedSideBar: -1, // 选中的侧边栏索引
  sideBarSearchKey: '', // 侧边栏搜索字段

  IsQCloudCos: true,
  IsTegCos: false,

  // 全局访问控制：匿名调用 /api/access-rules/me 返回的 blockedAuth（命中 match:'*' 通配规则）
  // 用于登录页等"未登录态"决定是否隐藏 AK/SK 入口。应用启动时主动拉取一次。
  globalBlockedAuth: [],
  // 上面那次拉取是否完成（不区分成功/失败，只表示"已知结果"）。
  // 登录页在 ready=false 时不渲染表单，避免先闪 AK 表单再切到 OAuth 的视觉跳变。
  accessRulesReady: false,

  fileLoading: false, // 文件列表 loading 状态
  SyncV4BucketAcl: true,
  SyncV4ObjectAcl: true,
  showUserInfo: true,
};
const store = Vue.observable(state);

/**
 * 初始化 mutations
 */
const mutations = {
  /**
   * 初始化面包屑导航
   */
  initCrumbs() {
    // HACK: 这里单独翻译，因为语言包依赖 store.js
    const name = store.localeLanguage === 'zh' ? '存储桶列表' : 'Bucket List';
    store.crumbs = [{ name, flag: 'root' }];
    saveStore(store);
  },

  getCrumbs() {
    return store.crumbs;
  },

  computeCrumbs(params) {
    const rootName = store.localeLanguage === 'zh' ? '存储桶列表' : 'Bucket List';
    const crumbs = [{ name: rootName, flag: 'root' }];
    if (!params.bucket) {
      store.crumbs = crumbs; // 修改：直接赋值避免遗漏
      saveStore(store);
      return;
    }

    const { bucket, region, path } = params;
    crumbs.push({ name: bucket, region, prefix: '' });

    const prefix = (path || '').replace(/^\//, '');
    let prefixKey = '';

    if (prefix) {
      prefix.split('/').forEach(name => {
        if (name) {
          prefixKey += `${name}/`;
          crumbs.push({ name, prefix: prefixKey });
        }
      });
    }
    store.crumbs = crumbs;
    saveStore(store);
  },

  /**
 * 保存全局信息
 */
  saveStore() {
    saveStore(store);
  },

  /**
   * 更改文件系统布局
   * @param {String} layout
   */
  switchLayout(layout) {
    store.layout = layout;
    saveStore(store);
  },

  /**
   * 登出后清空数据
   */
  initStoreAfterLogout() {
    store.isLogin = false;
    store.crumbs = [];
    // 用户信息
    store.userInfo = {};
    // 上传文件数
    store.uploadNum = 0;
    // 下载文件数
    store.downloadNum = 0;
    saveStore(store);
  },

  /**
   * 修改当前语言环境
   * @param {Sting} lan 语言类型
   */
  changeLocaleLanguage(lan) {
    // 语言有效性检查
    if (lan && supportedLanguages.includes(lan) && lan !== state.localeLanguage) {
      store.localeLanguage = lan;
      storage.add('general.Language', lan);
      saveStore(store);
      window.lang.locale = lan;
      // 同步更新 URL 参数 (修复 no-shadow)
      const newUrlParams = new URLSearchParams(window.location.search);
      newUrlParams.set('lang', lan);
      window.history.replaceState({}, '', `${window.location.pathname}?${newUrlParams}`);
    }
  },

  // 获取 store 的数据
  synchronousData(obj) {
    Object.assign(store, obj);
  },
};

if (storage.get('store')) {
  const sessionStore = storage.get('store', 'session');
  const obj = (sessionStore !== 'undefined' && sessionStore) || {};
  mutations.synchronousData(obj);
}

export { store, mutations };
